import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TagGroup, TagList } from "react-aria-components";
import { css } from "../../../../styled-system/css";
import Tag, { defaultTagColor } from "../../../components/core/basic/Tag";
import { useTagsQuery } from "../api/useTagsQuery";
import type { TagEntityType } from "../../../zodios/apiDefinitions/api/tags";

interface EntityTagListProps {
  entityType: TagEntityType;
  tagIds: number[];
  onRemove?: (tagId: number) => void;
  ariaLabel?: string;
}

export const EntityTagList = ({ entityType, tagIds, onRemove, ariaLabel }: EntityTagListProps) => {
  const { t } = useTranslation(["tags"]);
  const tagsQuery = useTagsQuery({ entityType });
  const resolvedAriaLabel = ariaLabel ?? t(($) => $.tags["Tags"]);

  const items = useMemo(() => {
    const byId = new Map((tagsQuery.data?.results ?? []).map((tag) => [tag.id, tag]));
    return tagIds
      .map((id) => {
        const tag = byId.get(id);
        if (!tag) return null;
        return { id: String(tag.id), label: tag.name, color: tag.color || defaultTagColor };
      })
      .filter((v): v is { id: string; label: string; color: string } => v !== null);
  }, [tagsQuery.data?.results, tagIds]);

  if (items.length === 0) return null;

  return (
    <TagGroup aria-label={resolvedAriaLabel}>
      <TagList items={items} className={css({ display: "flex", gap: "small", flexWrap: "wrap" })}>
        {(tag) => <Tag tag={tag} color={tag.color} onRemove={onRemove ? (id) => onRemove(Number(id)) : undefined} />}
      </TagList>
    </TagGroup>
  );
};
